import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import Api from '../utils/Api';
import { useError } from '../context/ErrorContext';
import { useLoading } from '../context/LoadingContext';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useRoutePath } from '../utils/getRoutePath';
  
const CompanyInfoContext = createContext();

export const useCompanyInfo = () => {
  const context = useContext(CompanyInfoContext);
  if (!context) {
    throw new Error('useCompanyInfo must be used within a CompanyInfoProvider');
  }
  return context;
};

export const CompanyInfoProvider = ({ children }) => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { hideLoading } = useLoading();
  const { getToken } = useAuth();
  const [api, setApi] = useState(null);
  const { handleApiError, formErrors, clearErrors } = useError();
  const [companyInfo, setCompanyInfo] = useState([]);
  const segments = location.pathname.split('/');
  const id = segments.pop();
  const [paginate,setPaginate] = useState(true)
  const getRoutePath = useRoutePath();
  const initialized = location.pathname === getRoutePath('/company-info')
  
  const getCompanyInfo = useCallback(async ( page = 1,filters = {}) => {
    
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
     
      return;
    }
   
    try {
      const searchParams = new URLSearchParams(window.location.search);
      let query = '';
      if(filters?.q){
        query = '&q='+filters?.q;
        
      } else{
        const q = searchParams.get('q') || '';
        query = '&q='+q;
      }
      if(filters?.active){
        query += '&active='+filters?.active;
      } else{
        const active = searchParams.get('active') || '';
        query += '&active='+active;
      }
    
      
      let response = await apiInstance.call('companies?page=' + page+'&paginate='+paginate +''+query , 'GET');
      if(filters?.all){
        query = '';
        setPaginate(false)
        response = await apiInstance.call('companies');  
      }
      
      
      if (response.status === 200) {
        
        setPaginate(response.data.paginate)
        setCompanyInfo(response.data.companies || []);
       
        hideLoading();
        if(filters){
          return response.data.companies;
        }
      } else {
        
        hideLoading();
        handleApiError(response.error, {
          showAlert: false,
          redirectOn401: true,
          customMessage: 'Failed to fetch company information'
        });
      }
    } catch (error) {
       
      hideLoading();
    } finally {
      
    }
  }, [handleApiError, hideLoading,paginate,getToken]);

  const getCompanyInfoByIdApi = useCallback(async (id,filters = {}) => {
    
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
     
      return;
    }
    const response = await apiInstance.call(`companies/${id}`, 'GET', null, true, true);
   
    if (response.status === 200) {
     
      let companies = {'data':[response.data.company]}
      setCompanyInfo(companies || []);
      
    }
  },[getToken]);
  

   useEffect(() => {
    const handleLoad = async() => {
      const currentPath = window.location.pathname;
      
      if(currentPath.includes(getRoutePath('/company-info/edit/'+id))){
          
        if (getToken()) {
          const apiInstance = Api(() => getToken());
      
          setApi(apiInstance);
          await getCompanyInfoByIdApi(id);
         
        }
      }
     
      
    };

    // Add onload listener
    window.addEventListener('load', handleLoad);

    // Cleanup listener
    return () => {
      window.removeEventListener('load', handleLoad);
    };
  }, [getRoutePath,id,getToken,getCompanyInfoByIdApi,searchParams]);

  // Initialize API and load initial data
  useEffect(() => {
    const initializeApi = async () => {
       
      try {
        const apiInstance = Api(() => getToken());
      
        setApi(apiInstance);

        if (getToken()) {
          let page = parseInt(searchParams.get('page')) || 1;
          await getCompanyInfo(page);
         
        }
      } catch (error) {
       
      }
    };
   
    if(initialized){
      
      initializeApi();
    }
  
    
  }, [getToken, getCompanyInfo, searchParams, initialized]);

  const addCompanyInfo = async (info) => {
    if (!api) {
      
      return;
    }

    const formData = new FormData();
    Object.keys(info).forEach(key => {
      if (info[key] !== null) {
        formData.append(key, info[key]);
      }
    });

    try {
      const response = await api.call('companies', 'POST', formData, true, true);
      if (response.status === 200) {
        const companies = response.data.companies;
        setCompanyInfo(companies);
        return { status: response.status };
      } else {
        if (response.error.status === 422) {
          return {
            status: response.error.status,
            errors: response.error.validationErrors
          };
        } else {
          return handleApiError(response.error, {
            showAlert: false,
            redirectOn401: true,
            customMessage: 'Failed to add company information'
          });
        }
      }
    } catch (error) {
     
      return handleApiError(error, {
        showAlert: true,
        redirectOn401: true,
        customMessage: 'Failed to add company information'
      });
    }
  };

  const getCompanyInfoById = (id) => {
   
    let companyInfoData = companyInfo?.data?.find(info => {
      return Number(info.id) === Number(id);
    });
     
    return companyInfoData;
  };
  

  const updateCompanyInfo = async (id, updatedInfo) => {
    if (!api) {
       
      return;
    }
   
    const formData = new FormData();
    Object.keys(updatedInfo).forEach(key => {
      if (updatedInfo[key] !== null) {
        formData.append(key, updatedInfo[key]);
      }
    });

    try {
      const response = await api.call(`companies/${id}`, 'POST', formData, true, true);
      if (response.status === 200) {
        const updatedCompanyInfo = companyInfo?.data?.map(info =>
          Number(info.id) === Number(id) ? {
            ...response.data.company,
          } : info
        );
        setCompanyInfo(updatedCompanyInfo);
        return { status: response.status };
      } else {
        if (response.error.status === 422) {
          return {
            status: response.error.status,
            errors: response.error.validationErrors
          };
        } else {
          return handleApiError(response.error, {
            showAlert: false,
            redirectOn401: true,
            customMessage: 'Failed to update company information'
          });
        }
      }
    } catch (error) {
      
      return handleApiError(error, {
        showAlert: false,
        redirectOn401: true,
        customMessage: 'Failed to update company information'
      });
    }
  };

  const deleteCompanyInfo = async (id) => {
    if (!api) {
      
      return;
    }

    try {
      const response = await api.call(`companies/${id}`, 'DELETE', null, true);
      if (response.status === 200) {
        navigate(getRoutePath('/company-info'));
        return { status: response.status };
      } else {
        return handleApiError(response.error, {
          showAlert: false,
          redirectOn401: true,
          customMessage: 'Failed to delete company information'
        });
      }
    } catch (error) {
      
      return handleApiError(error, {
        showAlert: true,
        redirectOn401: true,
        customMessage: 'Failed to delete company information'
      });
    }
  };

  return (
    <CompanyInfoContext.Provider
      value={{
        companyInfo,
        addCompanyInfo,
        getCompanyInfo: (page,filters) => getCompanyInfo( page,filters),
        getCompanyInfoById,
        updateCompanyInfo,
        deleteCompanyInfo,
        formErrors,
        paginate,
        clearErrors
      }}
    >
      {children}
    </CompanyInfoContext.Provider>
  );
}; 